import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileDown, Link2, Mail, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/swishr-central.png";

const logoUrl = /^(https?:|data:)/.test(logo) ? logo : `${window.location.origin}${logo}`;

// Builds a clean, branded, print-ready HTML document for the browser print engine.
const buildBrandedDoc = ({ workspaceName, preparedBy, docTitle, docSubtitle, bodyHtml }) => {
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  return `<!doctype html><html><head><meta charset="utf-8" /><title>${docTitle}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #1e293b; margin: 0; padding: 48px 56px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .brand { display:flex; align-items:center; justify-content:space-between; border-bottom: 3px solid #0f766e; padding-bottom: 16px; margin-bottom: 28px; }
  .brand .logo { height: 64px; width: auto; }
  .brand .ws { font-family: Arial, sans-serif; text-align:right; font-size: 12px; color:#64748b; }
  h1 { font-size: 26px; margin: 0 0 4px; }
  .sub { color:#64748b; font-size: 14px; margin: 0 0 24px; font-family: Arial, sans-serif; }
  table { width:100%; border-collapse: collapse; margin: 14px 0 24px; font-family: Arial, sans-serif; font-size: 13px; }
  th { text-align:left; background:#f1f5f9; color:#334155; padding: 9px 12px; border-bottom: 2px solid #e2e8f0; text-transform:uppercase; font-size:10px; letter-spacing:.5px; }
  td { padding: 9px 12px; border-bottom: 1px solid #eef2f6; }
  td.num, th.num { text-align:right; }
  .pill { display:inline-block; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight:700; background:#f1f5f9; color:#475569; }
  .totals { display:flex; gap: 32px; margin: 8px 0 24px; font-family: Arial, sans-serif; }
  .totals .box { background:#f8fafc; border:1px solid #e2e8f0; border-radius: 10px; padding: 14px 18px; }
  .totals .lbl { font-size: 10px; text-transform: uppercase; letter-spacing:.5px; color:#94a3b8; }
  .totals .val { font-size: 20px; font-weight: 700; margin-top: 4px; }
  .card { border:1px solid #e2e8f0; border-radius:10px; padding:14px 18px; margin-bottom:12px; font-family: Arial, sans-serif; }
  .card .k { font-size:10px; text-transform:uppercase; letter-spacing:.5px; color:#94a3b8; }
  .card .v { font-size:14px; margin-top:2px; color:#1e293b; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-family: Arial, sans-serif; font-size: 11px; color:#94a3b8; display:flex; justify-content:space-between; }
  @media print { body { padding: 24px 28px; } .noprint { display:none; } }
</style></head><body>
  <div class="brand"><img class="logo" src="${logoUrl}" alt="Swishr Central" /><span class="ws">${workspaceName || "Workspace"}<br/>Prepared ${today}</span></div>
  <h1>${docTitle}</h1>
  ${docSubtitle ? `<p class="sub">${docSubtitle}</p>` : ""}
  ${bodyHtml}
  <div class="footer"><span>Prepared by ${preparedBy || "—"}</span><span>Confidential · Swishr Central</span></div>
</body></html>`;
};

export const ShareDialog = ({ open, onOpenChange, docTitle, docSubtitle, bodyHtml, shareUrl, testidPrefix = "share" }) => {
  const { workspace, currentUser } = useFgp();
  const [copied, setCopied] = useState(false);

  const printPdf = () => {
    const html = buildBrandedDoc({ workspaceName: workspace?.name, preparedBy: currentUser?.name, docTitle, docSubtitle, bodyHtml });
    const w = window.open("", "_blank", "width=920,height=760");
    if (!w) return toast.error("Please allow pop-ups to export the PDF");
    w.document.open(); w.document.write(html); w.document.close(); w.focus();
    const img = w.document.querySelector(".brand .logo");
    const fire = () => { try { w.print(); } catch { /* */ } };
    if (img && !img.complete) { img.onload = fire; img.onerror = fire; setTimeout(fire, 1200); }
    else { setTimeout(fire, 400); }
    toast.success("PDF ready — use “Save as PDF” in the print dialog");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Shareable link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — select and copy the link manually");
    }
  };

  const emailIt = () => {
    const subject = encodeURIComponent(`${docTitle} — via Swishr Central`);
    const body = encodeURIComponent(`Hi,\n\nSharing "${docTitle}" from ${workspace?.name || "our workspace"}.\n\nView it here: ${shareUrl}\n\nBest,\n${currentUser?.name || ""}`);
    const a = document.createElement("a");
    a.href = `mailto:?subject=${subject}&body=${body}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Share2 className="h-5 w-5 text-neutral-600" /> Share</DialogTitle>
          <DialogDescription>Send “{docTitle}” to a principal or colleague.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <button onClick={printPdf} data-testid={`${testidPrefix}-pdf-btn`}
            className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-4 text-left hover:border-neutral-400 hover:bg-neutral-50 transition-colors">
            <span className="h-10 w-10 rounded-lg bg-neutral-100 text-neutral-700 flex items-center justify-center flex-shrink-0"><FileDown className="h-5 w-5" /></span>
            <span><span className="block text-sm font-semibold text-slate-800">Download PDF</span><span className="block text-xs text-slate-400">Branded, print-ready document</span></span>
          </button>

          <button onClick={emailIt} data-testid={`${testidPrefix}-email-btn`}
            className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-4 text-left hover:border-neutral-400 hover:bg-neutral-50 transition-colors">
            <span className="h-10 w-10 rounded-lg bg-neutral-100 text-neutral-700 flex items-center justify-center flex-shrink-0"><Mail className="h-5 w-5" /></span>
            <span><span className="block text-sm font-semibold text-slate-800">Email</span><span className="block text-xs text-slate-400">Opens your mail app with a ready draft</span></span>
          </button>

          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800"><Link2 className="h-4 w-4 text-neutral-600" /> Shareable link</div>
            <div className="flex items-center gap-2">
              <Input readOnly value={shareUrl} className="h-9 text-xs text-slate-500 bg-slate-50" data-testid={`${testidPrefix}-link-input`} onFocus={(e) => e.target.select()} />
              <Button size="sm" onClick={copyLink} className="h-9 gap-1.5 bg-neutral-600 hover:bg-neutral-700 flex-shrink-0" data-testid={`${testidPrefix}-copy-btn`}>
                {copied ? <><Check className="h-4 w-4" /> Copied</> : "Copy"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
